import {
  TableRow,
  TableCell,
  IconButton,
  Typography,
  Stack,
  Link,
  Autocomplete,
  TextField,
  Skeleton,
  Checkbox,
  Tooltip,
  Chip,
  Menu,
  MenuItem,
  useMediaQuery,
  useTheme
} from "@mui/material";
import { Dispatch, SetStateAction, useCallback, useEffect, useState } from "react";
import Iconify from "../../../minimals/components/iconify";
import { statusColorMap } from "../statusColorMap";
import { EventType, SelectedTasks } from "../types";
import axios from "axios";
import { StyledBadge } from "../../shared/StyledBadge";
import { Contact } from "../../shared/orderInfo/types";
import getRegularNotesCount from "../../utils/getRegularNotesCount";
import RequiredDocAlertModal from "../RequiredDocAlertModal";
import { fMonthDayYearDotNotation } from "../../utils/formatTime";
import { debounce } from "lodash";
import { DraggableProvided, DraggableStateSnapshot } from "@hello-pangea/dnd";
import { Category, NewTask, OmitOption } from "./types";
import RequirementInfo from "./RequirementInfo";
import ChecklistQaNoteCell from "../qa/ChecklistQaNoteCell";
import MoreVertIcon from "@mui/icons-material/MoreVert";

const TaskRow = ({
  t,
  eventTypes,
  refreshTasks,
  setSelectedTaskNotes,
  setSelectedTaskUploadDocument,
  setSelectedTaskViewDocuments,
  setSelectedTaskHistory,
  contacts,
  handleSelectTask,
  isSelected,
  provided,
  snapshot,
  categories,
  rowStyle,
  hiddenColumns,
  setStatusToUpdateAfterRequiredUpload
}: {
  t: NewTask;
  parentTask?: NewTask;
  eventTypes: EventType[];
  refreshTasks: () => void;
  selectedTaskNotes: NewTask | null;
  selectedTaskAssignment: NewTask | null;
  setSelectedTaskNotes: Dispatch<SetStateAction<NewTask | null>>;
  setSelectedAssignment: Dispatch<SetStateAction<NewTask | null>>;
  selectedTaskUploadDocument: NewTask | null;
  setSelectedTaskUploadDocument: Dispatch<SetStateAction<NewTask | null>>;
  selectedTaskViewDocuments: NewTask | null;
  setSelectedTaskViewDocuments: Dispatch<SetStateAction<NewTask | null>>;
  setSelectedTaskHistory: Dispatch<SetStateAction<NewTask | null>>;
  contacts: Contact[];
  handleSelectTask: (taskId: number, isChecked: boolean) => void;
  isSelected: boolean;
  selectedTasks: SelectedTasks;
  index: number;
  provided: DraggableProvided;
  snapshot: DraggableStateSnapshot;
  categories: Category[];
  rowStyle: object;
  hiddenColumns: string[];
  setStatusToUpdateAfterRequiredUpload: Dispatch<SetStateAction<number | null>>;
}) => {
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const [alertRequiredDocOpen, setAlertRequiredDocOpen] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("lg"));

  const updateTaskStatus = async (basicTaskId: number, option: OmitOption) => {
    if (t.isDocUploadRequired && option.statusApplicableTo === 3 && t.documents.length === 0) {
      setStatusToUpdateAfterRequiredUpload(option.id || null);
      setAlertRequiredDocOpen(true);
      return;
    }
    await axios.post("/api/taskmanagement/newtasks/updateStatus", {
      basicTaskId,
      status: option.id
    });
    refreshTasks();
  };

  const updateTaskCategory = async (basicTaskId: number, categoryId: number) => {
    await axios.post("/api/taskmanagement/newtasks/updateCategory", {
      basicTaskId,
      categoryId
    });
    refreshTasks();
  };

  const updateContactCode = async (contactCode: string | undefined) => {
    await axios.post("/api/taskmanagement/newtasks/setContactCode", {
      basicTaskId: t.id,
      contactCode
    });
    refreshTasks();
  };

  const saveQuickNote = async (taskId: number, note: string | null) => {
    await axios.post("/api/taskmanagement/newtasks/editQuickNote", {
      taskId,
      note
    });
    refreshTasks();
  };

  const debouncedSaveQuickNote = useCallback(
    debounce((taskId, note) => saveQuickNote(taskId, note), 300),
    []
  );

  useEffect(() => {
    if (contacts.length === 0) return;
    setSelectedContact(contacts.find((c) => c.code === t.contactCode) || null);
  }, [t.contactCode, contacts]);

  const isContactsLoading = contacts.length === 0;

  const isFlips = t.applicationId === 1;

  const latestEvent =
    t.updateHistories && t.updateHistories.length > 0 ? t.updateHistories[t.updateHistories.length - 1] : null;

  const selectedOmitOption = t.omitOptions.find((o) => o.id === t.currentOmitOptionId);

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleAction = (action: () => void) => {
    action();
    handleMenuClose();
  };

  return (
    <>
      <TableRow
        key={t.id}
        ref={provided.innerRef}
        sx={{
          "& .MuiInputBase-input": {
            fontSize: "0.85rem", // shrink font
            padding: "4px 6px" // reduce padding
          },
          "@media (max-width:1199px)": {
            "& .MuiInputBase-input:not(.checklist-qa-note)": {
              height: "16px !important",
              minHeight: "16px !important"
            }
          },
          "& .MuiInputLabel-root": {
            fontSize: "0.75rem"
          },
          ...provided.draggableProps.style,
          background: "#ffffff",
          border: snapshot.isDragging ? "1px dashed lightgreen" : "none",
          boxShadow: snapshot.isDragging ? "0 4px 8px 0 rgba(0, 0, 0, 0.2), 0 6px 20px 0 rgba(0, 0, 0, 0.15)" : "none",
          ...rowStyle
        }}
        {...provided.draggableProps}
        {...provided.dragHandleProps}
      >
        {!isFlips && (
          <TableCell padding="checkbox" sx={{ verticalAlign: "top", paddingTop: 1.2 }}>
            <Checkbox
              checked={isSelected}
              onChange={(event) => handleSelectTask(t.id, event.target.checked)}
              size="small"
            />
          </TableCell>
        )}
        <TableCell
          sx={{ verticalAlign: "top", width: 500, maxWidth: 500, wordWrap: "break-word", padding: "8px 16px" }}
        >
          {!t.parentTaskTag && (
            <Typography
              sx={{
                lineHeight: 1.3,
                fontSize: { xs: "0.75rem", lg: "0.875" }
              }}
              variant="subtitle2"
            >
              {t.description}
            </Typography>
          )}
          {t.parentTaskTag && (
            <Typography variant="subtitle2" display="inline-block" marginBottom={0.5}>
              {t.parentTaskTag && (
                <Chip
                  variant="soft"
                  size="small"
                  label={t.parentTaskTag}
                  sx={{
                    borderRadius: 1,
                    fontWeight: 600,
                    marginRight: 1
                  }}
                />
              )}
              {t.description}
            </Typography>
          )}
          {t.grandparentRequirementId && (
            <RequirementInfo
              requirementCode={t.grandparentRequirementCode}
              requirementNumber={t.grandparentRequirementNumber}
              requirementText={t.grandparentRequirementOriginalText}
            />
          )}
          {t.parentRequirementId && (
            <RequirementInfo
              requirementCode={t.parentRequirementCode}
              requirementNumber={
                t.grandparentRequirementNumber
                  ? t.parentRequirementNumber ?? t.childRequirementNumber
                  : t.parentRequirementNumber
              }
              requirementText={t.parentRequirementOriginalText}
            />
          )}
          {t.childRequirementCode && (
            <RequirementInfo
              requirementCode={t.childRequirementCode}
              requirementNumber={t.grandparentRequirementCode ? "" : t.childRequirementNumber}
              requirementText={t.childRequirementOriginalText}
            />
          )}
        </TableCell>
        {!hiddenColumns.includes("category") && (
          <TableCell sx={{ verticalAlign: "top" }}>
            {/* <Stack direction="row" spacing={0.5}> */}
            <Autocomplete
              sx={{
                width: { xs: 160, lg: 200 }, // Reduce from 200
                minWidth: 120,
                "@media (max-width:1199px)": {
                  ".MuiOutlinedInput-root": {
                    paddingRight: "24px !important"
                  }
                }
              }}
              value={categories.find((c) => c.id === t.categoryId) || null}
              onChange={(_, option) => option?.id && updateTaskCategory(t.id, option?.id)}
              options={categories}
              getOptionLabel={(option) => option.name}
              renderInput={(params) => <TextField {...params} size="small" variant="outlined" placeholder="Category" />}
            />
            {/* </Stack> */}
          </TableCell>
        )}
        {!hiddenColumns.includes("quickNote") && (
          <TableCell sx={{ verticalAlign: "top" }}>
            {/* <Stack direction="row" spacing={0.5}> */}
            <TextField
              defaultValue={t.quickNote}
              size="small"
              sx={{
                width: { xs: 140, lg: 200 },
                ".MuiInputBase-root": { padding: "4px 6px !important" }
              }}
              multiline
              placeholder="Quick Note"
              onChange={(e) => {
                debouncedSaveQuickNote(t.id, e.target.value);
              }}
              inputProps={{ maxLength: 170 }}
            />
            {/* </Stack> */}
          </TableCell>
        )}
        {!isFlips && <ChecklistQaNoteCell taskId={t.id} />}
        {isFlips && (
          <TableCell sx={{ verticalAlign: "top" }}>
            {latestEvent && (
              <>
                <Typography variant="body2" color="GrayText" noWrap>
                  <Link onClick={() => setSelectedTaskHistory(t)} sx={{ cursor: "pointer" }}>
                    {eventTypes.find((e) => e.id === latestEvent.eventType)?.description}
                  </Link>{" "}
                  <Typography variant="body2">
                    {latestEvent?.dateTime && fMonthDayYearDotNotation(latestEvent?.dateTime)}
                  </Typography>
                </Typography>
                <Typography variant="body2" color="GrayText">
                  by&nbsp;{latestEvent?.username}
                </Typography>
              </>
            )}
          </TableCell>
        )}

        {!isFlips && !hiddenColumns.includes("responsibleParty") && (
          <TableCell sx={{ verticalAlign: "top" }}>
            {isContactsLoading && (
              <>
                <Skeleton width={200} />
              </>
            )}
            {!isContactsLoading && (
              <Autocomplete
                sx={{
                  width: {
                    xs: 140,
                    lg: 200
                  },
                  "@media (max-width:1199px)": {
                    ".MuiOutlinedInput-root": {
                      paddingRight: "24px !important"
                    }
                  },
                  minWidth: 120
                }}
                renderInput={(params) => (
                  <TextField {...params} placeholder="Responsible Party" size="small" variant="outlined" />
                )}
                getOptionLabel={(o) => {
                  if (o.type === "Attorney" && o.represents) {
                    let { represents } = o;
                    if (represents.charAt(represents.length - 1) !== "s") {
                      represents += "s";
                    }
                    return `${represents} Attorney - ${o.name}`;
                  }
                  return `${o.code} - ${o.name || ""}`;
                }}
                options={contacts}
                value={selectedContact}
                onChange={(_, value) => updateContactCode(value?.code)}
              />
            )}
          </TableCell>
        )}
        {!hiddenColumns.includes("status") && (
          <TableCell sx={{ verticalAlign: "top" }}>
            <Autocomplete
              sx={{
                minWidth: { xs: 120, md: 150 },
                maxWidth: { xs: 180, md: 300 },
                "@media (max-width:1199px)": {
                  ".MuiOutlinedInput-root": {
                    paddingRight: "24px !important"
                  }
                },
                width: "100%"
              }}
              slotProps={{ popper: { placement: "bottom-start", sx: { minWidth: 300 } } }}
              value={selectedOmitOption}
              onChange={(_, option) => option?.id && updateTaskStatus(t.id, option)}
              options={t.omitOptions.filter((o) => !o.isInactive)}
              getOptionLabel={(option) => option.text}
              renderInput={(params) => (
                <Tooltip
                  title={selectedOmitOption?.statusApplicableTo === 3 ? selectedOmitOption.text : ""}
                  placement="top"
                >
                  <TextField
                    {...params}
                    size="small"
                    variant="outlined"
                    placeholder="Status"
                    sx={{
                      "& .MuiInputBase-input": {
                        color: statusColorMap[selectedOmitOption?.statusApplicableTo || ""],
                        fontSize: "0.875rem",
                        padding: "4px 8px",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis"
                      }
                    }}
                  />
                </Tooltip>
              )}
              renderOption={(props, option, { index }) => (
                <li
                  {...props}
                  key={option.id}
                  style={{
                    backgroundColor: "#f5f5f5",
                    transition: "background-color 0.3s",
                    cursor: "pointer"
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#e0e0e0")}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#f5f5f5")}
                >
                  <Typography color={statusColorMap[option.statusApplicableTo || ""]} sx={{ overflowWrap: "anywhere" }}>
                    {option.text}
                  </Typography>
                </li>
              )}
            />
          </TableCell>
        )}
        <TableCell
          sx={{
            width: { xs: "25px", lg: "200px" },
            minWidth: { xs: "25px", lg: "120px" },
            verticalAlign: "top"
          }}
        >
          {!isMobile ? (
            <Stack direction="row" spacing={0.5} justifyContent="flex-end">
              <Tooltip title="View Documents">
                <IconButton onClick={() => setSelectedTaskViewDocuments(t)}>
                  <StyledBadge badgeContent={t.documents?.length} color="info">
                    <Iconify icon="iconamoon:folder-document" />
                  </StyledBadge>
                </IconButton>
              </Tooltip>
              <Tooltip title="Upload Documents">
                <IconButton onClick={() => setSelectedTaskUploadDocument(t)}>
                  <Iconify icon="eva:upload-fill" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Add Notes">
                <IconButton onClick={() => setSelectedTaskNotes(t)}>
                  <StyledBadge
                    badgeContent={t.notes ? getRegularNotesCount(t.notes.map((n) => n.text)) : null}
                    color="error"
                  >
                    <Iconify icon="eva:file-add-outline" />
                  </StyledBadge>
                </IconButton>
              </Tooltip>
              <Tooltip title="View History">
                <IconButton onClick={() => setSelectedTaskHistory(t)}>
                  <Iconify icon="eva:clock-outline" />
                </IconButton>
              </Tooltip>
            </Stack>
          ) : (
            <Stack direction="row" justifyContent="flex-end">
              <IconButton onClick={handleMenuClick}>
                <MoreVertIcon />
              </IconButton>
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "right"
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "right"
                }}
              >
                <MenuItem onClick={() => handleAction(() => setSelectedTaskViewDocuments(t))}>
                  <Iconify icon="iconamoon:folder-document" sx={{ mr: 1 }} />
                  View Documents {t.documents?.length > 0 && `(${t.documents.length})`}
                </MenuItem>
                <MenuItem onClick={() => handleAction(() => setSelectedTaskUploadDocument(t))}>
                  <Iconify icon="eva:upload-fill" sx={{ mr: 1 }} />
                  Upload Documents
                </MenuItem>
                <MenuItem onClick={() => handleAction(() => setSelectedTaskNotes(t))}>
                  <Iconify icon="eva:file-add-outline" sx={{ mr: 1 }} />
                  Add Notes{" "}
                  {t.notes &&
                    getRegularNotesCount(t.notes.map((n) => n.text)) > 0 &&
                    `(${getRegularNotesCount(t.notes.map((n) => n.text))})`}
                </MenuItem>
                <MenuItem onClick={() => handleAction(() => setSelectedTaskHistory(t))}>
                  <Iconify icon="eva:clock-outline" sx={{ mr: 1 }} />
                  View History
                </MenuItem>
              </Menu>
            </Stack>
          )}
        </TableCell>
      </TableRow>

      <RequiredDocAlertModal
        open={alertRequiredDocOpen}
        closeModal={() => setAlertRequiredDocOpen(false)}
        setSelectedTaskUploadDocument={setSelectedTaskUploadDocument}
        task={t}
      />
    </>
  );
};

export default TaskRow;
